import { useState, useEffect } from "react"
import { propertyTypes } from "./create-property-data"
import { provinces } from "../../actions/global-actions-data"
import "./properties-filters.css"

function PropertiesFilters({
  currentFilters,
  onFiltersChange,
  availableOptions,
  onClearFilters,
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [filters, setFilters] = useState(currentFilters)
  const [citiesToShow, setCitiesToShow] = useState([])

  useEffect(() => {
    setFilters(currentFilters)
    // Actualizar ciudades cuando cambia el departamento
    if (currentFilters.department) {
      const dept = provinces.find((d) => d.name === currentFilters.department)
      setCitiesToShow(dept?.cities || [])
    } else {
      setCitiesToShow([])
    }
  }, [currentFilters])

  const handleFilterChange = (filterName, value) => {
    let newFilters = {
      ...filters,
      [filterName]: value,
    }

    // Si cambia el departamento, limpiar la ciudad
    if (filterName === "department") {
      newFilters.city = ""
      const dept = provinces.find((d) => d.name === value)
      setCitiesToShow(dept?.cities || [])
    }

    setFilters(newFilters)
    onFiltersChange(newFilters)
  }

  const handleClearClick = () => {
    const clearedFilters = {
      code: "",
      owner: "",
      propertyType: "",
      status: "",
      department: "",
      city: "",
      address: "",
      neighborhood: "",
    }
    setFilters(clearedFilters)
    setCitiesToShow([])
    onFiltersChange(clearedFilters)
    if (onClearFilters) {
      onClearFilters()
    }
  }

  const activeFiltersCount = Object.values(filters).filter(
    (value) => value !== "",
  ).length

  return (
    <div className="filters-wrapper">
      <button
        className="filters-header-btn"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
        </svg>
        <span>Filtros</span>
        {activeFiltersCount > 0 && (
          <span className="filters-badge">{activeFiltersCount}</span>
        )}
        <span className={`chevron ${isExpanded ? "open" : ""}`}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </span>
      </button>

      {isExpanded && (
        <div className="filters-panel">
          <div className="filters-grid">
            {/* Código */}
            <div className="filter-field">
              <label htmlFor="code-filter">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="21 8 21 21 3 21 3 8"></polyline>
                  <rect x="1" y="3" width="22" height="5"></rect>
                  <path d="M10 12v8"></path>
                  <path d="M14 12v8"></path>
                </svg>
                Código
              </label>
              <input
                id="code-filter"
                type="text"
                className="filter-input"
                placeholder="Ej: INM-001"
                value={filters.code || ""}
                onChange={(e) => handleFilterChange("code", e.target.value)}
              />
            </div>

            {/* Dirección */}
            <div className="filter-field">
              <label htmlFor="address-filter">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 10c0 7-7 13-7 13s-7-6-7-13a7 7 0 1 1 14 0Z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                Dirección
              </label>
              <input
                id="address-filter"
                type="text"
                className="filter-input"
                placeholder="Ej: Calle 5"
                value={filters.address || ""}
                onChange={(e) => handleFilterChange("address", e.target.value)}
              />
            </div>

            {/* Barrio */}
            <div className="filter-field">
              <label htmlFor="neighborhood-filter">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                  <polyline points="9 22 9 12 15 12 15 22"></polyline>
                </svg>
                Barrio
              </label>
              <input
                id="neighborhood-filter"
                type="text"
                className="filter-input"
                placeholder="Ej: Centro"
                value={filters.neighborhood || ""}
                onChange={(e) =>
                  handleFilterChange("neighborhood", e.target.value)
                }
              />
            </div>

            {/* Propietario */}
            <div className="filter-field">
              <label htmlFor="owner-filter">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                Propietario
              </label>
              <select
                id="owner-filter"
                className="filter-select"
                value={filters.owner || ""}
                onChange={(e) => handleFilterChange("owner", e.target.value)}
              >
                <option value="">Todos</option>
                {availableOptions?.owners?.map((owner) => (
                  <option key={owner._id} value={owner._id}>
                    {owner.fullName}
                  </option>
                ))}
              </select>
            </div>

            {/* Tipo de Propiedad */}
            <div className="filter-field">
              <label htmlFor="propertyType-filter">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                  <polyline points="9 22 9 12 15 12 15 22"></polyline>
                </svg>
                Tipo
              </label>
              <select
                id="propertyType-filter"
                className="filter-select"
                value={filters.propertyType || ""}
                onChange={(e) =>
                  handleFilterChange("propertyType", e.target.value)
                }
              >
                <option value="">Todos</option>
                {propertyTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Estado */}
            <div className="filter-field">
              <label htmlFor="status-filter">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="1"></circle>
                  <circle cx="19" cy="12" r="1"></circle>
                  <circle cx="5" cy="12" r="1"></circle>
                </svg>
                Estado
              </label>
              <select
                id="status-filter"
                className="filter-select"
                value={filters.status || ""}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="">Todos</option>
                <option value="En contrato">En contrato</option>
                <option value="Sin alquilar">Sin alquilar</option>
              </select>
            </div>

            {/* Departamento */}
            <div className="filter-field">
              <label htmlFor="department-filter">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                Departamento
              </label>
              <select
                id="department-filter"
                className="filter-select"
                value={filters.department || ""}
                onChange={(e) =>
                  handleFilterChange("department", e.target.value)
                }
              >
                <option value="">Todos</option>
                {provinces.map((dept) => (
                  <option key={dept.name} value={dept.name}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Ciudad (solo se muestra si hay departamento seleccionado) */}
            <div className="filter-field">
              <label htmlFor="city-filter">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                  <polyline points="9 22 9 12 15 12 15 22"></polyline>
                </svg>
                Ciudad
              </label>
              <select
                id="city-filter"
                className="filter-select"
                value={filters.city || ""}
                onChange={(e) => handleFilterChange("city", e.target.value)}
                disabled={!filters.department}
              >
                <option value="">Todas</option>
                {citiesToShow.map((city) => (
                  <option key={city.name} value={city.name}>
                    {city.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {activeFiltersCount > 0 && (
            <div className="filters-footer">
              <button className="clear-btn" onClick={handleClearClick}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="1"></circle>
                  <path d="m19 5-14 14M5 5l14 14"></path>
                </svg>
                Limpiar filtros
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default PropertiesFilters
